import type { FlowFieldConfig, FlowStepConfig } from './types'

/**
 * Extracts the set of "pivot" field keys for a given step.
 *
 * A pivot field is any field in stepId whose key is referenced by:
 * - `visibleWhen.field`
 * - `visibleWhenAll[].field`
 * - `visibleWhenAny[][].field`
 * - `remoteOptions.dependsOn`
 *
 * …of any field in a step with id > stepId.
 *
 * When a pivot field's value changes, subsequent steps may show/hide
 * different fields or load different remote options, so their data
 * should be invalidated.
 */
export function getPivotFields(steps: FlowStepConfig[], stepId: number): Set<string> {
  // Collect all field keys that belong to stepId (and all previous steps,
  // since conditions can reference fields from any earlier step)
  const fieldsInCurrentAndBefore = new Set<string>()
  for (const step of steps) {
    if (step.id > stepId) break
    for (const field of step.fields) {
      fieldsInCurrentAndBefore.add(field.key)
    }
  }

  // Collect all field keys referenced in conditions/dependsOn of later steps
  const referencedKeys = new Set<string>()

  for (const step of steps) {
    if (step.id <= stepId) continue
    for (const field of step.fields) {
      collectReferencedFields(field, referencedKeys)
    }
  }

  // Pivot fields = fields in the current step that are referenced downstream
  const currentStepFields = new Set<string>()
  const currentStep = steps.find((s) => s.id === stepId)
  if (currentStep) {
    for (const field of currentStep.fields) {
      currentStepFields.add(field.key)
    }
  }

  // Return the intersection: fields in current step that are referenced by later steps
  const pivotFields = new Set<string>()
  for (const key of currentStepFields) {
    if (referencedKeys.has(key)) {
      pivotFields.add(key)
    }
  }

  return pivotFields
}

function collectReferencedFields(field: FlowFieldConfig, refs: Set<string>) {
  if (field.visibleWhen) {
    refs.add(field.visibleWhen.field)
  }
  if (field.visibleWhenAll) {
    for (const cond of field.visibleWhenAll) {
      refs.add(cond.field)
    }
  }
  if (field.visibleWhenAny) {
    for (const group of field.visibleWhenAny) {
      for (const cond of group) {
        refs.add(cond.field)
      }
    }
  }
  if (field.remoteOptions?.dependsOn) {
    refs.add(field.remoteOptions.dependsOn)
  }
}
